import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api-client";

export default function NotificationRouting() {
  const [routes, setRoutes] = useState<any[]>([
    { id: "r1", service: "api-prod", source: "sentry", channel: "slack", destination: "#incidents" },
    { id: "r2", service: "api-prod", source: "datadog", channel: "slack", destination: "#alerts-infra" }
  ]);
  const [selectedService, setSelectedService] = useState("");
  const [selectedSource, setSelectedSource] = useState("sentry");
  const [selectedDestination, setSelectedDestination] = useState("");

  const { data: services, isLoading } = useQuery({
    queryKey: ["services"],
    queryFn: () => api.getServices()
  });

  const handleAddRoute = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService || !selectedDestination.trim()) {
      toast.error("Please configure all routing rule settings fields.");
      return;
    }

    const newRoute = {
      id: Math.random().toString(),
      service: selectedService,
      source: selectedSource,
      channel: "slack",
      destination: selectedDestination.trim()
    };

    setRoutes(prev => [...prev, newRoute]);
    setSelectedDestination("");
    toast.success("Routing logic mapped successfully!");
  };

  const handleDeleteRoute = (routeId: string) => {
    setRoutes(prev => prev.filter(r => r.id !== routeId));
    toast.success("Routing logic rule disconnected.");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 font-mono text-xs select-none">
      <div className="border-b-2 border-black pb-2 mb-4">
        <h3 className="text-sm font-black uppercase tracking-tight">Signal Relay Routing Console</h3>
        <p className="text-[10px] text-zinc-400 uppercase font-black">Map alert source streams directly to Slack channels</p>
      </div>

      <div className="space-y-4">
        {routes.map(r => (
          <div key={r.id} className="border-2 border-black p-4 bg-zinc-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="font-black text-black uppercase">{r.service}</span>
              <span className="text-[10px] bg-black text-white px-1 border border-black">{r.source}</span>
              <ArrowRight className="w-3.5 h-3.5" />
              <span className="text-zinc-500 font-bold uppercase">{r.channel} {r.destination}</span>
            </div>
            <button onClick={() => handleDeleteRoute(r.id)} className="text-red-500 hover:text-red-700">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <form onSubmit={handleAddRoute} className="border-4 border-black p-6 bg-white space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase text-zinc-400">Target Service</label>
            <select
              value={selectedService}
              onChange={e => setSelectedService(e.target.value)}
              className="w-full h-10 border-2 border-black rounded-none px-3 bg-white text-xs uppercase font-bold"
            >
              <option value="">-- Choose Service --</option>
              {services?.map(s => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase text-zinc-400">Context Source</label>
            <select
              value={selectedSource}
              onChange={e => setSelectedSource(e.target.value)}
              className="w-full h-10 border-2 border-black rounded-none px-3 bg-white text-xs uppercase font-bold"
            >
              <option value="sentry">Sentry Project</option>
              <option value="datadog">Datadog ID</option>
              <option value="grafana">Grafana labels</option>
              <option value="url">URL Monitor check</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase text-zinc-400">Relay destination channel</label>
            <input
              required
              type="text"
              placeholder="#alerts-prod"
              value={selectedDestination}
              onChange={e => setSelectedDestination(e.target.value)}
              className="w-full h-10 border-2 border-black rounded-none px-3 bg-white text-xs uppercase font-bold"
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full h-11 rounded-none border-2 border-black bg-black text-white hover:bg-white hover:text-black font-black uppercase text-xs tracking-wider shadow-[3px_3px_0_0_#000]"
        >
          Add Routing Rule
        </Button>
      </form>
    </div>
  );
}
